import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, Handshake, Radar, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STAGES, STAGE_LABELS, STAGE_COLORS } from "./config";

const ICONS = { Radar, AlertTriangle, Handshake, CheckCircle2 };

export default function CrmDashboard() {
  const [data, setData] = useState(null);
  const [followUps, setFollowUps] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/crm/dashboard")
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/crm/follow-ups").then(({ data: f }) => setFollowUps(f)).catch(() => setFollowUps({ count: 0, alerts: [], delay_days: 5 }));
  }, []);

  if (error) return <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="crm-dashboard-error">{error}</p>;
  if (!data) return <p className="text-sm text-steel">Chargement…</p>;

  const funnelMax = Math.max(1, ...STAGES.map((s) => data.funnel[s] || 0));
  const cards = [
    { label: "Nouveaux leads (30 j)", value: data.new_leads_30d, icon: "Radar", to: "/crm/leads", testid: "crm-kpi-new-leads" },
    { label: "Tâches en retard", value: data.overdue_tasks, icon: "AlertTriangle", to: "/crm/taches", warn: data.overdue_tasks > 0, testid: "crm-kpi-overdue-tasks" },
    { label: "Opportunités actives", value: data.active_opportunities, icon: "Handshake", to: "/crm/opportunites", testid: "crm-kpi-active-opportunities" },
    { label: "Conversions gagnées (30 j / total)", value: `${data.won_30d} / ${data.won_total}`, icon: "CheckCircle2", to: "/crm/opportunites", testid: "crm-kpi-won" },
  ];

  return (
    <div className="space-y-8" data-testid="crm-dashboard">
      <div>
        <h1 className="font-display text-2xl font-bold text-navy-950">Tableau de bord CRM</h1>
        <p className="mt-1 text-sm text-steel">Vue d'ensemble commerciale — pipeline, relances et conversions.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, icon, to, warn, testid }) => {
          const Icon = ICONS[icon];
          return (
            <Link key={testid} to={to} data-testid={testid}
              className="rounded-2xl border border-line bg-white p-5 shadow-card transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex items-center justify-between">
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${warn ? "bg-red-50 text-red-600" : "bg-badge text-sky-700"}`}>
                  <Icon size={18} />
                </span>
                <span className={`font-display text-3xl font-bold ${warn ? "text-red-600" : "text-navy-950"}`}>{value}</span>
              </div>
              <p className={`mt-3 text-[13px] font-semibold ${warn ? "text-red-600" : "text-graphite"}`}>{label}</p>
            </Link>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-line bg-white p-6 shadow-card lg:col-span-2" data-testid="crm-funnel">
          <h2 className="text-sm font-bold uppercase tracking-wider text-steel">Pipeline commercial</h2>
          <div className="mt-5 space-y-3">
            {STAGES.map((s) => (
              <div key={s} className="flex items-center gap-3">
                <span className="w-44 shrink-0 text-xs font-semibold text-graphite">{STAGE_LABELS[s]}</span>
                <div className="h-6 flex-1 overflow-hidden rounded-full bg-paper">
                  <div className={`flex h-full items-center justify-end rounded-full px-2 text-[11px] font-bold text-white transition-all duration-500 ${s === "gagne" ? "bg-emerald-500" : s === "perdu" ? "bg-red-400" : "bg-electric"}`}
                    style={{ width: `${Math.max(((data.funnel[s] || 0) / funnelMax) * 100, (data.funnel[s] || 0) > 0 ? 8 : 0)}%` }}>
                    {(data.funnel[s] || 0) > 0 && data.funnel[s]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-line bg-white p-6 shadow-card" data-testid="crm-followups-panel">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-steel">
              <AlertTriangle size={14} className={(followUps?.count || 0) > 0 ? "text-amber-500" : "text-emerald-500"} />
              Relances à faire — demandes de prix sans réponse depuis {followUps?.delay_days ?? "…"} jour(s)
            </h2>
            {followUps === null ? (
              <p className="mt-4 text-sm text-steel">Chargement…</p>
            ) : followUps.count === 0 ? (
              <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">Aucune relance en attente. Rappel interne seulement — aucun courriel automatique.</p>
            ) : (
              <ul className="mt-4 space-y-2">
                {followUps.alerts.slice(0, 5).map((a) => (
                  <li key={a.inquiry_id} className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-sm" data-testid={`crm-followup-alert-${a.inquiry_id}`}>
                    <p className="font-semibold text-amber-800">{a.supplier_name} — {a.request_number}</p>
                    <p className="text-xs text-amber-700">Envoyée depuis {a.days_waiting} jours · relance suggérée</p>
                  </li>
                ))}
                {followUps.count > 5 && <li className="text-xs text-steel">+ {followUps.count - 5} autre(s) — visibles dans le panneau MOON de chaque lead.</li>}
              </ul>
            )}
          </div>

          <div className="rounded-2xl border border-line bg-white p-6 shadow-card" data-testid="crm-overdue-list">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-steel">
              <AlertTriangle size={14} className={data.overdue_tasks > 0 ? "text-red-500" : "text-emerald-500"} /> Tâches en retard
            </h2>
            {data.overdue_list.length === 0 ? (
              <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">Aucune tâche en retard. Tout est à jour !</p>
            ) : (
              <ul className="mt-4 space-y-2">
                {data.overdue_list.map((t) => (
                  <li key={t.id} className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm">
                    <p className="font-semibold text-red-700">{t.title}</p>
                    <p className="text-xs text-red-500">Échéance : {t.due_date}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-line bg-white p-5 shadow-card" data-testid="crm-kpi-companies">
              <Building2 size={18} className="text-sky-700" />
              <p className="mt-2 font-display text-2xl font-bold text-navy-950">{data.total_companies}</p>
              <p className="text-xs font-semibold text-steel">Entreprises</p>
            </div>
            <div className="rounded-2xl border border-line bg-white p-5 shadow-card" data-testid="crm-kpi-contacts">
              <Users size={18} className="text-sky-700" />
              <p className="mt-2 font-display text-2xl font-bold text-navy-950">{data.total_contacts}</p>
              <p className="text-xs font-semibold text-steel">Contacts</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
